import React, { Fragment, PropsWithChildren, useMemo } from 'react';
import { getUserLang, isEuEnv } from '@mb/lib';
import { Box, Grid, Link, Skeleton } from '@mui/material';
import { parseISO, isWithinInterval, startOfDay, endOfDay } from 'date-fns';
import { palette, pxToEm } from 'theme';
import { formatBrandsBasedOnCategories } from './BrandsLinks.helpers';
import * as Styled from './BrandsLinks.styles';
import { BrandLinkType, BrandsLinksProps } from './BrandsLinks.types';
import { Status } from '../../../typings/enums';

const userLang = getUserLang();
const prefix = isEuEnv ? `/${userLang}` : '';

const AVAILABLE_BRAND_STATUS = '1';
const COMING_SOON_BRAND_STATUS = '1';

const RowOfLinksSkeleton = (
  <Grid item xs={12} sm={6} md={2} display="flex" flexDirection="column">
    <Box>
      {new Array(6).fill('').map((_, index) => (
        <Skeleton animation="wave" key={index}>
          <Link>Some brand name name name</Link>
        </Skeleton>
      ))}
    </Box>
  </Grid>
);

const BrandsLinksSkeleton = (
  <Fragment>
    {new Array(3).fill('').map((_, index) => (
      <Fragment key={index}>
        <Styled.BrandsDivider />
        <Skeleton animation="wave">
          <Styled.CategoryTitle>Category</Styled.CategoryTitle>
        </Skeleton>
        <Grid container rowGap={2} columnGap={3} marginTop={pxToEm(6)}>
          {RowOfLinksSkeleton}
          {RowOfLinksSkeleton}
          {index === 1 ? [RowOfLinksSkeleton] : null}
          {index === 2 ? [RowOfLinksSkeleton, RowOfLinksSkeleton] : null}
        </Grid>
      </Fragment>
    ))}
  </Fragment>
);

const BrandLink: React.FC<BrandLinkType> = ({
  href,
  text,
  isComingSoon,
  isAvailable,
  labels,
}) => (
  <Styled.BrandLink
    href={href ?? ''}
    isAvailable={isAvailable}
    data-testid="brands-link"
  >
    <Styled.BrandLinkText>
      {isComingSoon ? (
        <span>
          {text}&nbsp;
          <Styled.Label bgColor={palette.lightBlue.main}>
            {labels?.soon ?? 'Soon'}
          </Styled.Label>
        </span>
      ) : (
        <span>{text}</span>
      )}
    </Styled.BrandLinkText>
  </Styled.BrandLink>
);
const BrandsLinks: React.FC<PropsWithChildren<BrandsLinksProps>> = ({
  brands,
  status,
  children,
  labels,
}) => {
  const formattedBrands = useMemo(
    () => formatBrandsBasedOnCategories(brands),
    [brands],
  );
  const currentDateF = startOfDay(new Date());

  if (status !== Status.LOADED) {
    return BrandsLinksSkeleton;
  }

  return (
    <Fragment>
      {children}
      {formattedBrands.map(([category, brands], index) => (
        <Fragment key={category}>
          <Styled.BrandsDivider isDesktopHidden={index === 0} />
          <Styled.CategoryTitleLink underline="none" id={category}>
            <Styled.CategoryTitle data-testid="brands-category">
              {category}
            </Styled.CategoryTitle>
          </Styled.CategoryTitleLink>
          <Styled.BrandsColumns>
            {brands.map((brand) => {
              const isComingSoon =
                brand.coming_soon === COMING_SOON_BRAND_STATUS;
              const brandName = brand.manufacturer_name;
              const isAvailable =
                brand.status === AVAILABLE_BRAND_STATUS && !isComingSoon;
              const addonFilter = '#/filterValueLimiter=true';

              const isBrandNew = (() => {
                if (!brand.brand_new_from || !brand.brand_new_to) return false;

                const brandNewFrom = startOfDay(parseISO(brand.brand_new_from));
                const brandNewTo = endOfDay(parseISO(brand.brand_new_to));
                return isWithinInterval(currentDateF, {
                  start: brandNewFrom,
                  end: brandNewTo,
                });
              })();

              return (
                <Box key={brand.manufacturer_name} width="100%" py={pxToEm(4)}>
                  <Styled.BrandItem>
                    <BrandLink
                      href={prefix + brand.search_for_button + addonFilter}
                      text={brandName}
                      isComingSoon={isComingSoon}
                      isAvailable={isAvailable}
                      labels={labels}
                    />
                    {isBrandNew ? (
                      <Styled.Label>{labels.new}</Styled.Label>
                    ) : null}
                  </Styled.BrandItem>
                </Box>
              );
            })}
          </Styled.BrandsColumns>
        </Fragment>
      ))}
    </Fragment>
  );
};

export default React.memo(BrandsLinks);
